import * as go from 'gojs';
import { noop } from 'vet-bones/utils';

import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DELETED_BACKGROUND_COLOR,
  DELETED_OUTLINE_COLOR,
  DRAFT_BACKGROUND_COLOR,
  DRAFT_BACKGROUND_OUTLINE_WIDTH,
  DRAFT_OUTLINE_COLOR,
  LINK_COLOR,
  LINK_LABEL_BACKGROUND_COLOR,
  LINK_LABEL_FONT_FACE,
  LINK_LABEL_FONT_SIZE,
  LINK_LABEL_FONT_WEIGHT,
  LINK_LABEL_MAX_SIZE,
  LINK_LABEL_PADDING,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isHierarchyModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  determineValueFromDraft,
  isHierarchyVisible,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { ModelConceptHierarchyLabelNode } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { getDiagramPartLabel } from 'src/containers/ProjectDiagram/utils/labels';

const $ = go.GraphObject.make;

export const createModelConceptHierarchyLabelTemplate = ({
  deleted = false,

  contextMenu,
  handleClick = noop,
  handleDoubleClick = noop,
  toolTip,
}: {
  deleted?: boolean;

  contextMenu: go.HTMLInfo;
  handleClick?: NonNullable<ModelConceptHierarchyLabelNode['click']>;
  handleDoubleClick?: NonNullable<
    ModelConceptHierarchyLabelNode['doubleClick']
  >;
  toolTip: go.HTMLInfo;
}) =>
  $(
    ExtendedNode,
    'Spot',
    {
      click: handleClick,
      contextMenu,
      cursor: 'pointer',
      doubleClick: handleDoubleClick,
      layerName: Layer.MODEL_CONCEPT_HIERARCHY_LABEL_BACKGROUND_LAYER,
      mouseEnter: graphObjectMouseEnter,
      mouseLeave: graphObjectMouseLeave,
      movable: false,
      selectionAdorned: false,
      toolTip,
    },
    modelBinding<go.Shape, ModelData>()('visible', '', isHierarchyVisible),
    objectBinding<go.Node, go.Node>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MODEL_CONCEPT_HIERARCHY_LABEL_FOREGROUND_LAYER
        : Layer.MODEL_CONCEPT_HIERARCHY_LABEL_BACKGROUND_LAYER
    ),

    $(
      go.Panel, // label container
      'Auto',
      {
        name: 'LABEL',
      },

      // white background
      $(
        go.Shape,
        'RoundedRectangle',
        {
          name: 'LABEL BACKGROUND',
          fill: LINK_LABEL_BACKGROUND_COLOR,
          stroke: LINK_LABEL_BACKGROUND_COLOR,
          strokeWidth: DRAFT_BACKGROUND_OUTLINE_WIDTH,
        },
        modelBinding<go.Shape, ModelData>()(
          'fill',
          '',
          determineValueFromDraft(
            isHierarchyModified,
            deleted ? DELETED_BACKGROUND_COLOR : LINK_LABEL_BACKGROUND_COLOR,
            deleted ? DELETED_BACKGROUND_COLOR : DRAFT_BACKGROUND_COLOR
          )
        ),
        modelBinding<go.Shape, ModelData>()(
          'stroke',
          '',
          determineValueFromDraft(
            isHierarchyModified,
            deleted ? DELETED_OUTLINE_COLOR : LINK_LABEL_BACKGROUND_COLOR,
            deleted ? DELETED_OUTLINE_COLOR : DRAFT_OUTLINE_COLOR
          )
        )
      ),

      $(
        go.TextBlock, // the actual label compoent
        {
          name: 'LABEL TEXT',
          stroke: LINK_COLOR,
          background: 'transparent',
          font: `${LINK_LABEL_FONT_WEIGHT} ${LINK_LABEL_FONT_SIZE}px ${LINK_LABEL_FONT_FACE}`,
          wrap: go.Wrap.None,
          margin: new go.Margin(0, LINK_LABEL_PADDING),
          overflow: go.TextOverflow.Ellipsis,
        },
        modelBinding<go.TextBlock, ModelData>()(
          'text',
          'labelsByIri',
          getDiagramPartLabel
        ),
        modelBinding<go.TextBlock, ModelData>()(
          'stroke',
          '',
          determineColorFromSelection(isHierarchyModified, LINK_COLOR)
        ),
        objectBinding<go.TextBlock, go.Node>()('scale', '', (node) =>
          node.isHighlighted || node.isSelected ? 1.2 : 1
        ),
        objectBinding<go.TextBlock, go.Node>()(
          'maxSize',
          '',
          (node) =>
            new go.Size(
              node.isHighlighted || node.isSelected ? NaN : LINK_LABEL_MAX_SIZE,
              NaN
            )
        )
      )
    )
  );
